import { useState, useMemo } from "react";
import { AppLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import {
  useListLocations,
  useListLocationAvailability,
  useListTrainers,
  type TrainerProfile,
  type AvailabilityBlock,
} from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Clock, Calendar, Star, Building2, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "wouter";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatTimeDisplay(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m.toString().padStart(2, "0")} ${period}`;
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function clipBlockToGymHours(
  block: AvailabilityBlock,
  gymDayBlocks: { startTime: string; endTime: string }[]
): AvailabilityBlock[] {
  if (gymDayBlocks.length === 0) return [];
  const bStart = timeToMinutes(block.startTime);
  const bEnd = timeToMinutes(block.endTime);
  const clipped: AvailabilityBlock[] = [];
  for (const gh of gymDayBlocks) {
    const gStart = timeToMinutes(gh.startTime);
    const gEnd = timeToMinutes(gh.endTime);
    const overlapStart = Math.max(bStart, gStart);
    const overlapEnd = Math.min(bEnd, gEnd);
    if (overlapStart < overlapEnd) {
      clipped.push({
        ...block,
        startTime: minutesToTime(overlapStart),
        endTime: minutesToTime(overlapEnd),
      });
    }
  }
  return clipped;
}

function TrainerScheduleCard({
  trainer,
  availability,
  gymId,
  gymHoursByDay,
}: {
  trainer: TrainerProfile;
  availability: AvailabilityBlock[];
  gymId: string;
  gymHoursByDay: Record<number, { startTime: string; endTime: string }[]>;
}) {
  const trainerBlocks = availability.filter(a => a.trainerId === trainer.id && a.isActive);
  const blocksByDay = useMemo(() => {
    const map: Record<number, AvailabilityBlock[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    trainerBlocks.forEach(b => {
      const clipped = clipBlockToGymHours(b, gymHoursByDay[b.dayOfWeek] || []);
      clipped.forEach(cb => map[cb.dayOfWeek].push(cb));
    });
    return map;
  }, [trainerBlocks, gymHoursByDay]);

  const hasAnyAvailability = Object.values(blocksByDay).some(arr => arr.length > 0);

  if (!hasAnyAvailability) return null;

  return (
    <Card className="glass-panel border-white/5 hover:border-primary/30 transition-all">
      <CardContent className="p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center text-lg font-bold text-white shrink-0 overflow-hidden">
            {trainer.avatarUrl ? (
              <img src={trainer.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              trainer.firstName?.[0] || "?"
            )}
          </div>
          <div className="flex-1 min-w-0">
            <Link href={trainer.publicSlug ? `/trainer/${trainer.publicSlug}` : `/book?trainerId=${trainer.id}&gymId=${gymId}`}>
              <h3 className="text-lg font-bold text-white truncate hover:text-primary transition-colors cursor-pointer">
                {trainer.firstName} {trainer.lastName}
              </h3>
            </Link>
            <div className="flex items-center gap-2 mt-0.5">
              {trainer.avgRating && (
                <span className="flex items-center gap-1 text-sm text-primary">
                  <Star className="w-3.5 h-3.5 fill-primary" />
                  {Number(trainer.avgRating).toFixed(1)}
                </span>
              )}
              {trainer.hourlyRate && (
                <span className="text-sm font-mono text-primary font-bold">${trainer.hourlyRate}/hr</span>
              )}
            </div>
          </div>
          <Link href={`/book?trainerId=${trainer.id}&gymId=${gymId}`}>
            <Button size="sm" className="bg-primary text-black font-bold rounded-xl shrink-0">
              Book <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {DAYS_SHORT.map((day, i) => {
            const dayBlocks = blocksByDay[i];
            const hasSlots = dayBlocks.length > 0;
            return (
              <div key={i} className="text-center">
                <div className={`text-[10px] font-semibold mb-1 ${hasSlots ? "text-white" : "text-muted-foreground/50"}`}>
                  {day}
                </div>
                {hasSlots ? (
                  <div className="space-y-0.5">
                    {dayBlocks.map((b, idx) => (
                      <div
                        key={idx}
                        className="text-[9px] text-primary bg-primary/10 rounded px-0.5 py-0.5 leading-tight"
                        title={`${formatTimeDisplay(b.startTime)} – ${formatTimeDisplay(b.endTime)}`}
                      >
                        {formatTimeDisplay(b.startTime).replace(/ (AM|PM)/, "").replace(":00", "")}
                        –
                        {formatTimeDisplay(b.endTime).replace(/ (AM|PM)/, "").replace(":00", "")}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[9px] text-muted-foreground/30">—</div>
                )}
              </div>
            );
          })}
        </div>

        {trainer.specialties && trainer.specialties.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {trainer.specialties.slice(0, 3).map(s => (
              <span key={s} className="px-2 py-0.5 bg-white/5 rounded-lg text-[10px] text-muted-foreground border border-white/10">
                {s}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function GymSchedule() {
  const { user } = useAuth();
  const gymId = user?.gymId || "";
  const today = new Date().getDay();
  const [selectedDay, setSelectedDay] = useState<number>(today);

  const { data: locations = [], isLoading: locationsLoading } = useListLocations(gymId, {
    query: { enabled: !!gymId },
  });

  const primaryLocation = locations.find(l => l.isPrimary && l.isActive) ?? locations.find(l => l.isActive);
  const { data: locationBlocks = [], isLoading: blocksLoading } = useListLocationAvailability(
    primaryLocation?.id ?? "",
    { query: { enabled: !!primaryLocation } }
  );

  const { data: trainers = [], isLoading: trainersLoading } = useListTrainers(gymId, undefined, {
    query: { enabled: !!gymId },
  });

  const { data: allTrainerAvailability = [], isLoading: availLoading } = useQuery<AvailabilityBlock[]>({
    queryKey: ["all-trainer-availability", gymId],
    queryFn: async () => {
      if (!trainers || trainers.length === 0) return [];
      const results: AvailabilityBlock[] = [];
      for (const t of trainers) {
        try {
          const blocks = await apiFetch(`/availability/trainer/${t.id}`) as AvailabilityBlock[];
          if (Array.isArray(blocks)) results.push(...blocks);
        } catch {}
      }
      return results;
    },
    enabled: !!gymId && trainers.length > 0,
  });

  const gymHoursByDay = useMemo(() => {
    const map: Record<number, { startTime: string; endTime: string }[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    locationBlocks.filter(b => b.isActive).forEach(b => {
      map[b.dayOfWeek].push({ startTime: b.startTime, endTime: b.endTime });
    });
    return map;
  }, [locationBlocks]);

  const trainersForDay = useMemo(() => {
    const dayGymBlocks = gymHoursByDay[selectedDay] || [];
    return trainers.filter(t => {
      return allTrainerAvailability.some(a => {
        if (a.trainerId !== t.id || a.dayOfWeek !== selectedDay || !a.isActive) return false;
        return clipBlockToGymHours(a, dayGymBlocks).length > 0;
      });
    });
  }, [trainers, allTrainerAvailability, selectedDay, gymHoursByDay]);

  const isLoading = locationsLoading || blocksLoading || trainersLoading || availLoading;

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12 max-w-5xl mx-auto">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Gym Schedule</h1>
          <p className="text-muted-foreground text-lg">
            Browse operating hours and find available trainers
          </p>
          {primaryLocation && (
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <Building2 className="w-4 h-4" />
              <span className="text-white font-medium">{primaryLocation.name}</span>
            </div>
          )}
        </div>

        <Card className="glass-panel border-white/5">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-white">Operating Hours</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3">
                {DAYS.map((day, i) => {
                  const blocks = gymHoursByDay[i] || [];
                  const isToday = i === today;
                  return (
                    <div
                      key={i}
                      className={`text-center p-3 rounded-xl ${
                        isToday ? "bg-primary/10 border border-primary/20" : "bg-white/[0.03]"
                      }`}
                    >
                      <div className={`text-xs font-semibold mb-1 ${isToday ? "text-primary" : "text-white"}`}>
                        {day.slice(0, 3)}
                        {isToday && <span className="text-[9px] ml-1 opacity-70">Today</span>}
                      </div>
                      {blocks.length === 0 ? (
                        <div className="text-xs text-red-400">Closed</div>
                      ) : (
                        blocks.map((b, idx) => (
                          <div key={idx} className="text-xs text-muted-foreground">
                            {formatTimeDisplay(b.startTime)}
                            <br />
                            {formatTimeDisplay(b.endTime)}
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-white">Trainers by Day</h2>
          </div>

          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {DAYS.map((day, i) => {
              const gymOpen = (gymHoursByDay[i] && gymHoursByDay[i].length > 0);
              const isToday = i === today;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDay(i)}
                  disabled={!gymOpen}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                    selectedDay === i
                      ? "bg-primary text-black font-bold"
                      : gymOpen
                        ? "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                        : "bg-white/[0.02] text-muted-foreground/30 border border-white/5 cursor-not-allowed"
                  }`}
                >
                  {day.slice(0, 3)}
                  {isToday && <span className="text-[9px] ml-1 opacity-70">(Today)</span>}
                </button>
              );
            })}
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 bg-white/5 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : trainersForDay.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/50 text-lg">No trainers available on {DAYS[selectedDay]}</p>
              <p className="text-muted-foreground text-sm mt-2">
                Try selecting a different day to find available trainers
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {trainersForDay.map(trainer => (
                <TrainerScheduleCard
                  key={trainer.id}
                  trainer={trainer}
                  availability={allTrainerAvailability}
                  gymId={gymId}
                  gymHoursByDay={gymHoursByDay}
                />
              ))}
            </div>
          )}
        </div>

        <div className="text-center pt-4">
          <Link href="/book">
            <Button variant="outline" className="border-white/10 rounded-xl">
              <ChevronRight className="w-4 h-4 mr-2" />
              Go to Full Booking
            </Button>
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
